import { REVIZTO_CONTROLS_PRESET } from "lib/cesium/controls/controlsPresetRevizto";
import { CESIUM_CONTROLS_PRESET } from "lib/cesium/controls/controlsPresetCesium";
import {
  compareToolState,
  SerializableToolInterface,
} from "./SerializableToolInterface";
import { Viewer } from "cesium";
import {
  ControlsPreset,
  ControlsPresetKey,
} from "lib/cesium/controls/ControlsPreset";
import { AttachablePluginInterface } from "./CesiumWrapper";
import {
  ObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";
import CameraControlsPlugin from "./CameraControlsPlugin";

export type ControlsPresetSnapshot = {
  version: 1;
  key: ControlsPresetKey;
};

export const CONTROLS_PRESETS: Record<ControlsPresetKey, ControlsPreset> = {
  cesium: CESIUM_CONTROLS_PRESET,
  revizto: REVIZTO_CONTROLS_PRESET,
};

export const DEFAULT_CONTROLS_PRESET = CONTROLS_PRESETS.revizto.key;

export default class ControlsPresetToolPlugin
  implements
    AttachablePluginInterface,
    SerializableToolInterface<ControlsPresetSnapshot>
{
  private _viewer: Viewer = null;

  private readonly _activePresetKey = new ObservableProperty<ControlsPresetKey>(
    DEFAULT_CONTROLS_PRESET,
  );

  readonly activePresetKey: ReadonlyObservableProperty<ControlsPresetKey> =
    this._activePresetKey;

  constructor(private readonly _cameraControlsPlugin: CameraControlsPlugin) {}

  setViewer(viewer: Viewer): void {
    this._viewer = viewer;
    this._applyToViewer(this._activePresetKey.getSnapshot());
  }

  detachViewer(): void {
    this._viewer = null;
  }

  applyPreset(key: ControlsPresetKey): void {
    this._activePresetKey.set(key);
    if (this._viewer) this._applyToViewer(key);
  }

  capture(): ControlsPresetSnapshot {
    return { version: 1, key: this._activePresetKey.getSnapshot() };
  }

  restore(snapshot: ControlsPresetSnapshot | undefined): void {
    const key = snapshot?.key;
    this.applyPreset(
      key && key in CONTROLS_PRESETS ? key : DEFAULT_CONTROLS_PRESET,
    );
  }

  isStateApplied(state: ControlsPresetSnapshot | undefined): boolean {
    return !!state && state.key !== DEFAULT_CONTROLS_PRESET;
  }

  isMatchingState(state: ControlsPresetSnapshot | undefined): boolean {
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  private _applyToViewer(key: ControlsPresetKey): void {
    const preset = CONTROLS_PRESETS[key];
    if (!preset) throw new Error(`Controls preset not found: ${key}`);
    const { mappings } = preset;
    this._cameraControlsPlugin.setControls({
      pan: mappings.pan.cesiumInputs,
      orbit: mappings.orbitAroundPoint.cesiumInputs,
      lookAround: mappings.lookAround.cesiumInputs,
      zoom: mappings.zoom.cesiumInputs,
    });
  }
}
